import React, { useMemo, useState } from 'react';
import { Badge } from '../ui/badge';
import { formatCurrency } from '../../lib/formatters';
import { DEAL_STATUSES } from '../../hooks/useDeals';

// Dot colors per status (MCA Dark concept: colored dot + count per column).
const STATUS_DOT = {
  'Funded': 'bg-green-500',
  'Payment Issues': 'bg-amber-500',
  'In-House Collection': 'bg-orange-500',
  'Default': 'bg-red-500',
  'Collected': 'bg-blue-500',
  'Paid in Full': 'bg-purple-500',
  'Paid in Full (Refinanced)': 'bg-indigo-500',
  'Paid in Full / Settled': 'bg-teal-500',
  'Cancelled': 'bg-gray-500',
};

/**
 * Kanban board for deals. Native HTML5 drag & drop (no extra deps).
 * Dropping a card on another column calls onStatusChange(deal, newStatus);
 * the parent confirms with the operator and PATCHes the deal. This component
 * never mutates data itself.
 */
const DealsKanban = ({ deals, onStatusChange, onCardClick, getStatusBadgeColor }) => {
  const [dragOverCol, setDragOverCol] = useState(null);

  const byStatus = useMemo(() => {
    const map = Object.fromEntries(DEAL_STATUSES.map((s) => [s, []]));
    for (const d of deals) {
      if (map[d.status]) map[d.status].push(d);
      // Deals with legacy/unknown statuses are not shown on the board;
      // the List view remains the complete source of truth.
    }
    return map;
  }, [deals]);

  const handleDragStart = (e, deal) => {
    e.dataTransfer.setData('text/plain', deal.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = (e, status) => {
    e.preventDefault();
    setDragOverCol(null);
    const dealId = e.dataTransfer.getData('text/plain');
    const deal = deals.find((d) => d.id === dealId);
    if (deal && deal.status !== status) {
      onStatusChange(deal, status);
    }
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-3 flex-1 min-h-0" data-testid="deals-kanban">
      <p className="sr-only">Drag cards between columns to update deal status.</p>
      {DEAL_STATUSES.map((status) => (
        <div
          key={status}
          className={`flex flex-col w-64 flex-shrink-0 rounded-lg border bg-gray-50 ${
            dragOverCol === status ? 'ring-2 ring-blue-500 border-blue-400' : 'border-gray-200'
          }`}
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
          onDragEnter={() => setDragOverCol(status)}
          onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null); }}
          onDrop={(e) => handleDrop(e, status)}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`h-2 w-2 rounded-full flex-shrink-0 ${STATUS_DOT[status] || 'bg-gray-400'}`} />
              <span className="text-sm font-medium truncate" title={status}>{status}</span>
            </div>
            <Badge variant="secondary" className="text-xs">{byStatus[status].length}</Badge>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[120px]">
            {byStatus[status].length === 0 ? (
              <p className="text-xs text-gray-400 text-center pt-6">No deals</p>
            ) : (
              byStatus[status].map((deal) => (
                <div
                  key={deal.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, deal)}
                  onClick={() => onCardClick(deal)}
                  className="rounded-md border border-gray-200 bg-white p-3 cursor-pointer hover:border-blue-400 hover:shadow-sm transition-colors"
                  data-testid={`kanban-card-${deal.deal_id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold truncate" title={deal.deal_name}>
                      {deal.deal_name || 'Unnamed deal'}
                    </p>
                    <span className="text-xs text-gray-500 font-mono flex-shrink-0">#{deal.deal_id}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
                    <span>Funded {formatCurrency(deal.funding_amount || 0)}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs">
                    <span className="text-gray-500">Balance</span>
                    <span className="font-medium">{formatCurrency(deal.balance || 0)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DealsKanban;
